import {
  Box,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { AnalyticsEvents, analytics } from "common/analytics";
import AnimalPerformanceIcon from "common/icons/AnimalPerformanceIcon";
import BiodiversityIcon from "common/icons/BiodiversityIcon";
import GrazingManagementIcon from "common/icons/GrazingManagementIcon";
import WaterAndSoilQualityIcon from "common/icons/WaterAndSoilQualityIcon";
import { SubmittedActionEntry } from "model";

// Category mapping: category_ids -> group key
export const getGroupKeyForCategory = (categoryId: number): string | null => {
  // 9, 7, 6, 3 -> "Soil_and_Water"
  if ([9, 7, 6, 3].includes(categoryId)) {
    return "Soil_and_Water";
  }
  // 5, 4 -> "Grazing_and_Farm_Management"
  if ([5, 4].includes(categoryId)) {
    return "Grazing_and_Farm_Management";
  }
  // 8 -> "Animal_Performance"
  if (categoryId === 8) {
    return "Animal_Performance";
  }
  // 1, 2 -> "Farmland_Biodiversity"
  if ([1, 2].includes(categoryId)) {
    return "Farmland_Biodiversity";
  }
  return null;
};

// Format key to readable title
export const formatTitle = (key: string) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Get formatted category name from category_id
export const getFormattedCategoryName = (categoryId: number): string | null => {
  const groupKey = getGroupKeyForCategory(categoryId);
  if (!groupKey) {
    return null;
  }
  return formatTitle(groupKey);
};

// Icon mapping based on group keys
const getIconForKey = (key: string) => {
  const iconMap = {
    Soil_and_Water: <WaterAndSoilQualityIcon />,
    Grazing_and_Farm_Management: <GrazingManagementIcon />,
    Animal_Performance: <AnimalPerformanceIcon />,
    Farmland_Biodiversity: <BiodiversityIcon />,
  };

  return iconMap[key as keyof typeof iconMap] || "📋";
};

type ActionListProps = {
  actionPlanEntries: SubmittedActionEntry[];
};

export default function ActionList({ actionPlanEntries }: ActionListProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Group actions by their merged category groups
  const groupedActions: Record<string, SubmittedActionEntry[]> = {};

  actionPlanEntries.forEach((entry) => {
    const groupKey = getGroupKeyForCategory(entry.categoryId);
    if (groupKey) {
      if (!groupedActions[groupKey]) {
        groupedActions[groupKey] = [];
      }
      groupedActions[groupKey].push(entry);
    }
  });

  return (
    <Box
      sx={{
        maxWidth: isMobile ? 600 : "100%",
        mx: isMobile ? "auto" : 0,
        bgcolor: "white",
        pt: 2,
      }}
    >
      {Object.entries(groupedActions).map(([key, entries]) => {
        if (entries && entries.length > 0) {
          return (
            <Box
              key={key}
              sx={{
                ...(isMobile
                  ? { paddingTop: "20px" }
                  : {
                      mb: 2,
                      borderRadius: "16px",
                      bgcolor: "#FFFFFF",
                      overflow: "hidden",
                      boxShadow:
                        "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
                    }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  py: 2.5,
                  px: 1,
                  bgcolor: "#FFFFFF",
                  //cursor: "pointer",
                  //"&:hover": {
                  /// bgcolor: "rgba(0, 0, 0, 0.02)",
                  //},
                }}
                // onClick={() => setSelectedValue(key)}
              >
                {/* Icon */}
                <Box
                  sx={{
                    fontSize: "28px",
                    minWidth: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {getIconForKey(key)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "16px",
                      mb: 0.5,
                      color: "#000",
                    }}
                  >
                    {formatTitle(key)}
                  </Typography>
                </Box>
              </Box>

              {entries.map((entry, index) => {
                // Use detail if available, otherwise use action
                const description = entry.action || "";

                if (description) {
                  return (
                    <React.Fragment key={entry.guid}>
                      {/* Divider between items */}
                      {index !== 0 && <Divider sx={{ mx: 1 }} />}

                      <Box
                        key={entry.guid}
                        onClick={() => {
                          analytics.track(
                            AnalyticsEvents.ACTION_DETAIL_VIEWED,
                            {
                              actionId: entry.guid,
                            }
                          );
                          navigate(`/action-details/${entry.guid}`);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          py: 2.5,
                          px: 1,
                          bgcolor: "#FFFFFF",
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: "rgba(0, 0, 0, 0.02)",
                          },
                        }}
                      >
                        {/* Text Content */}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "black",
                              fontSize: "14px",
                              lineHeight: 1.5,
                              paddingX: 2,
                            }}
                          >
                            {description}
                          </Typography>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                } else {
                  return <React.Fragment key={entry.guid} />;
                }
              })}
            </Box>
          );
        } else {
          return <React.Fragment key={key} />;
        }
      })}

      {/* Footer Text */}
      {/* <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          color: "#999",
          mt: 3,
          fontSize: "14px",
        }}
      >
        Click the activity to register as completed
      </Typography>*/}
    </Box>
  );
}

// Example usage:
// <ActionList selectedActions={selectedActions} />
